import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-13359">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-13360">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-13361">
              <span className="text-xs font-bold text-indigo-800" data-id="element-13362">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-13363">
            <div className="text-center" data-id="element-13364">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13365">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-13366">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-13367">→</div>
            <div className="text-center" data-id="element-13368">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13369">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-13370">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-13371">
            <table className="w-full text-sm" data-id="element-13372">
              <thead data-id="element-13373">
                <tr className="bg-indigo-100/70" data-id="element-13374">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13375">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13376">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-13377">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-13378">
                      <td className="px-3 py-1.5" data-id="element-13379">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-13380">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-13381">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13382">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13383">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-13384">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13385">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-13386">
            <span className="text-2xl" data-id="element-13387">📝</span>
            <div className="text-left" data-id="element-13388">
              <div className="text-sm font-bold text-indigo-900" data-id="element-13389">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-13390">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-13391">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-13392">
            <div className="text-center" data-id="element-13393">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13394">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-13395">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-13396">→</div>
            <div className="text-center" data-id="element-13397">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13398">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-13399">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-13400">
          <table className="w-full" data-id="element-13401">
            <thead data-id="element-13402">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-13403">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13404">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13405">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-13406">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-13407">
                    <td className="px-4 py-3" data-id="element-13408">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-13409">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-13410">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13411">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-13412">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-13413">
            <div className="flex items-start gap-3" data-id="element-13414">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-13415">
                <span className="text-blue-600 text-lg" data-id="element-13416">🔍</span>
              </div>
              <div data-id="element-13417">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-13418">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13419">
                  De <strong data-id="element-13420">stam</strong> krijg je door <strong data-id="element-13421">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-13422">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-13423">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13424">
              <div className="flex items-start gap-3" data-id="element-13425">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13426">
                  <span className="text-emerald-600 text-lg" data-id="element-13427">📋</span>
                </div>
                <div data-id="element-13428">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13429">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13430">
                    <p data-id="element-13431">
                      <strong data-id="element-13432">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13433">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-13434">
                      <strong data-id="element-13435">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13436">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13437">
                      <strong data-id="element-13438">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13439">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13440">
                      <strong data-id="element-13441">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13442">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13443">
              <div className="flex items-start gap-3" data-id="element-13444">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13445">
                  <span className="text-emerald-600 text-lg" data-id="element-13446">📋</span>
                </div>
                <div data-id="element-13447">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13448">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13449">
                    <p data-id="element-13450">
                      <strong data-id="element-13451">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-13452">
                      <strong data-id="element-13453">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13454">
                      <em data-id="element-13455">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13456">
              <div className="flex items-start gap-3" data-id="element-13457">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13458">
                  <span className="text-emerald-600 text-lg" data-id="element-13459">📋</span>
                </div>
                <div data-id="element-13460">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13461">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13462">
                    <p data-id="element-13463">
                      <strong data-id="element-13464">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-13465">
                      <strong data-id="element-13466">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13467">
                      <em data-id="element-13468">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13469">
            <div className="flex items-start gap-3" data-id="element-13470">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13471">
                <span className="text-amber-600 text-lg" data-id="element-13472">💡</span>
              </div>
              <div data-id="element-13473">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13474">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13475">
                  Bij <strong data-id="element-13476">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-13477">jij loopt</em> wordt{' '}
                  <em data-id="element-13478">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}